import { ref } from 'vue';
import PostSaleCasesAPI from 'dashboard/api/postSaleCases';
import { usePostSaleCaseNotes } from './usePostSaleCaseNotes';
import { usePostSaleCaseTopics } from './usePostSaleCaseTopics';

export function usePostSaleCaseDetail({ t, notify, refresh }) {
  const loadingDetail = ref(false);
  const selectedCase = ref(null);
  const responsibleUserId = ref('');
  const postSaleNotes = usePostSaleCaseNotes({ t, notify });
  const postSaleTopics = usePostSaleCaseTopics({ t, notify });

  const clear = () => {
    selectedCase.value = null;
    responsibleUserId.value = '';
    postSaleNotes.clear();
    postSaleTopics.clear();
  };

  const errorMessage = requestError =>
    requestError?.response?.data?.message ||
    requestError?.response?.data?.error ||
    t('POST_SALE.ALERTS.LOAD_ERROR');

  const markViewed = async postSaleCaseId => {
    try {
      const { data } = await PostSaleCasesAPI.view(postSaleCaseId);
      selectedCase.value = data;
      await refresh();
    } catch {
      return null;
    }
    return selectedCase.value;
  };

  const load = async postSaleCaseId => {
    loadingDetail.value = true;

    try {
      const { data } = await PostSaleCasesAPI.show(postSaleCaseId);
      selectedCase.value = data;
      responsibleUserId.value = data?.responsible?.id?.toString() || '';
      markViewed(data.id);
      await Promise.all([
        postSaleNotes.load(data.id),
        postSaleTopics.load(data.id),
      ]);
    } catch (requestError) {
      clear();
      notify(errorMessage(requestError));
    } finally {
      loadingDetail.value = false;
    }
  };

  const transition = async payload => {
    if (!selectedCase.value) return null;

    try {
      const { data } = await PostSaleCasesAPI.transition(
        selectedCase.value.id,
        payload
      );
      selectedCase.value = data;
      await refresh();
      return data;
    } catch (requestError) {
      notify(errorMessage(requestError));
      throw requestError;
    }
  };

  const assign = async () => {
    if (!selectedCase.value) return;

    try {
      const { data } = await PostSaleCasesAPI.assign(selectedCase.value.id, {
        responsible_user_id: responsibleUserId.value || undefined,
      });
      selectedCase.value = data;
      await refresh();
    } catch (requestError) {
      notify(errorMessage(requestError));
      throw requestError;
    }
  };

  return {
    assign,
    clear,
    load,
    loadingDetail,
    markViewed,
    responsibleUserId,
    selectedCase,
    transition,
    ...postSaleNotes,
    closeTopicForm: postSaleTopics.closeEditor,
    loadingTopics: postSaleTopics.loading,
    openTopicCreate: postSaleTopics.openCreate,
    openTopicEdit: postSaleTopics.openEdit,
    removeTopic: postSaleTopics.remove,
    saveTopic: postSaleTopics.save,
    selectedTopic: postSaleTopics.selectedTopic,
    showTopicForm: postSaleTopics.showTopicForm,
    topicCatalog: postSaleTopics.catalog,
    topicSaving: postSaleTopics.saving,
    topics: postSaleTopics.topics,
  };
}
